/**
 * @fileoverview Keeps a filter cell's editor state in step with the grid's model
 *
 * An edit is staged and reaches the model after a pause in editing, when the caller flushes it (the
 * editor closes or the user confirms) or the owning scope stops, or at once when the caller says so; a model the grid
 * sends back unchanged is an echo and is ignored, any other model replaces the editor state and drops a staged edit.
 */

import type { MaybeRefOrGetter } from "vue";
import { onScopeDispose, toValue, watch } from "vue";

import { FILTER_APPLY_DEBOUNCE_MS } from "./constants";
import type { ColFilterGroup } from "./filterModel";

export interface FilterModelSyncOptions {
  model: MaybeRefOrGetter<ColFilterGroup[] | null>;
  onModelChange: (model: ColFilterGroup[] | null) => void;
  /** The model the editor state stands for. */
  build: () => ColFilterGroup[] | null;
  /** Replaces the editor state with a model the editor did not emit. Runs once on setup. */
  adopt: (model: ColFilterGroup[] | null | undefined) => void;
}

const modelKey = (model: ColFilterGroup[] | null | undefined) => JSON.stringify(model ?? null);

export function useFilterModelSync(options: FilterModelSyncOptions) {
  // The model the editor state stands for: the last one emitted or adopted.
  let currentKey: string | undefined;
  let staged = false;
  let applyTimer: ReturnType<typeof setTimeout> | undefined;

  function unstage() {
    staged = false;
    clearTimeout(applyTimer);
  }

  function emitModel(model: ColFilterGroup[] | null) {
    const key = modelKey(model);
    if (key === currentKey) return;
    currentKey = key;
    options.onModelChange(model);
  }

  function apply() {
    unstage();
    emitModel(options.build());
  }

  /** Marks the editor state as edited; it reaches the model once editing pauses, or earlier on {@link flush}. */
  function stage() {
    staged = true;
    clearTimeout(applyTimer);
    applyTimer = setTimeout(flush, FILTER_APPLY_DEBOUNCE_MS);
  }

  /** Applies a staged edit, e.g. when the editor closes. Also runs when the owning scope stops. */
  function flush() {
    if (staged) apply();
  }

  // An unmounted dropdown emits no `closed`, so a cell removed while its popup is open would lose the edit.
  onScopeDispose(flush);

  /** Emits `null` at once; the caller empties its own state. */
  function clear() {
    unstage();
    emitModel(null);
  }

  function adopt(model: ColFilterGroup[] | null | undefined) {
    unstage();
    currentKey = modelKey(model);
    options.adopt(model);
  }

  /** Rebuilds the editor state from the current model, e.g. after its format changed. */
  function readopt() {
    adopt(toValue(options.model));
  }

  watch(
    () => toValue(options.model),
    (model) => {
      if (modelKey(model) !== currentKey) adopt(model);
    },
    { deep: true, immediate: true },
  );

  return { stage, apply, flush, clear, readopt };
}
